import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Users, Search, Trash2, Upload, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import LearnerJourneyPanel, { fetchLearnerJourney } from './LearnerJourneyPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

// Segments › Cohorts: view, search, add, remove and bulk-import learners.
export default function CohortRosterDialog({ cohort, open, onOpenChange, onChanged }) {
  const [learners, setLearners] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!cohort) return;
    setLoading(true);
    try {
      const r = await api.get(`/segments/cohorts/${cohort.id}/roster?limit=1000`);
      setLearners(r.data.learners || []); setCount(r.data.count || 0);
    } catch { setLearners([]); setCount(0); }
    finally { setLoading(false); }
  }, [cohort]);
  useEffect(() => { if (open) { setQ(''); load(); } }, [open, load]);

  const add = async (e) => {
    e?.preventDefault();
    const em = email.trim();
    if (!em) return;
    setBusy('add');
    try {
      await api.post(`/segments/cohorts/${cohort.id}/roster`, { rows: [em.includes('@') ? { email: em, name: name.trim() } : { learner_id: em, name: name.trim() }] });
      setEmail(''); setName('');
      await load(); onChanged?.();
    } catch (err) { toast.error(`Could not add: ${err.response?.data?.detail || err.message}`); }
    finally { setBusy(null); }
  };
  const remove = async (key) => {
    setBusy(key);
    try { await api.delete(`/segments/cohorts/${cohort.id}/roster/${encodeURIComponent(key)}`); await load(); onChanged?.(); }
    catch (err) { toast.error(`Could not remove: ${err.response?.data?.detail || err.message}`); }
    finally { setBusy(null); }
  };
  const importCsv = async (file) => {
    setBusy('import');
    try {
      const lines = (await file.text()).split(/\r?\n/).filter((l) => l.trim() && !l.startsWith('#'));
      const header = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/^"|"$/g, ''));
      const [ie, il, iname, iloc, iten, isd] = ['email', 'learner_id', 'name', 'location', 'tenure', 'start_date'].map((k) => header.indexOf(k));
      if (ie === -1 && il === -1) throw new Error('CSV needs an "email" or "learner_id" column');
      const rows = lines.slice(1).map((l) => l.split(',').map((v) => v.trim().replace(/^"|"$/g, ''))).map((v) => ({ email: ie >= 0 ? v[ie] : '', learner_id: il >= 0 ? v[il] : '', name: iname >= 0 ? v[iname] : '', location: iloc >= 0 ? v[iloc] : '', tenure: iten >= 0 ? v[iten] : '', start_date: isd >= 0 ? v[isd] : '' }));
      const r = await api.post(`/segments/cohorts/${cohort.id}/roster`, { rows });
      toast.success(`Roster: ${r.data.added} added, ${r.data.updated} updated${r.data.skipped ? `, ${r.data.skipped} skipped` : ''}`);
      await load(); onChanged?.();
    } catch (err) { toast.error(`Import failed: ${err.response?.data?.detail || err.message}`); }
    finally { setBusy(null); }
  };

  const [journey, setJourney] = useState(null);
  const openJourney = async (l) => {
    setJourney({ loading: true, key: l.learner_key });
    try { setJourney(await fetchLearnerJourney(l.learner_key, cohort.organization_id)); }
    catch (err) { setJourney(null); toast.error(`Could not load journey: ${err.response?.data?.detail || err.message}`); }
  };
  const shown = learners.filter((l) => !q || `${l.name || ''} ${l.email || ''} ${l.learner_id || ''}`.toLowerCase().includes(q.toLowerCase()));
  if (!cohort) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="cohort-roster-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Users className="h-4 w-4" /> Roster · {cohort.name} <span className="text-xs font-normal text-slate-500" data-testid="cohort-roster-count">{count} learner{count === 1 ? '' : 's'}</span></DialogTitle>
          <DialogDescription className="text-xs">Learners in this cohort power roster-weighted programme LEI and cohort-link suggestions.</DialogDescription>
        </DialogHeader>
        <form onSubmit={add} className="flex gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email or learner id" className="h-8 text-xs" data-testid="cohort-roster-add-email" />
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="name (optional)" className="h-8 text-xs w-40" data-testid="cohort-roster-add-name" />
          <Button type="submit" size="sm" className="h-8" disabled={busy === 'add' || !email.trim()} data-testid="cohort-roster-add-btn">{busy === 'add' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}</Button>
          <label className="h-8 px-2 inline-flex items-center gap-1 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 cursor-pointer" title="Import CSV (email / learner_id, name; optional location, tenure or start_date)" data-testid="cohort-roster-import-label">
            {busy === 'import' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} CSV
            <input type="file" accept=".csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importCsv(f); }} data-testid="cohort-roster-import-input" />
          </label>
        </form>
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search learners…" className="h-8 pl-7 text-xs" data-testid="cohort-roster-search" />
        </div>
        <LearnerJourneyPanel journey={journey} onClose={() => setJourney(null)} />
        {loading ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="cohort-roster-list">
            {shown.length === 0 && <li className="text-xs text-slate-500 py-6 text-center" data-testid="cohort-roster-empty">{count === 0 ? 'No learners yet — add one above or import a CSV.' : 'No matches.'}</li>}
            {shown.map((l) => (
              <li key={l.learner_key} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs" data-testid={`cohort-roster-row-${l.learner_key}`}>
                <button type="button" onClick={() => openJourney(l)} className="min-w-0 text-left hover:text-teal-700" title="View learner journey" data-testid={`cohort-roster-journey-${l.learner_key}`}><p className="font-medium text-slate-800 truncate">{l.name || l.email || l.learner_id}</p><p className="text-slate-500 truncate">{l.email || l.learner_id}</p></button>
                <button type="button" onClick={() => remove(l.learner_key)} disabled={busy === l.learner_key} className="text-slate-400 hover:text-rose-600 disabled:opacity-50" title="Remove" data-testid={`cohort-roster-remove-${l.learner_key}`}>{busy === l.learner_key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}</button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
